<script setup lang="ts">
import { ToastClose, ToastDescription, ToastRoot, ToastTitle } from 'reka-ui'
import { computed } from 'vue'
import type { SourceToastEntry, SourceToastVariant } from '~/composables/ui/useSourceToast'
import { useSourceToast } from '~/composables/ui/useSourceToast'

defineOptions({ name: 'SourceToastItem' })

const props = defineProps<{
  item: SourceToastEntry
  queueIndex: number
}>()

const { onToastOpenChange } = useSourceToast()

const isQueueHead = computed(() => props.queueIndex === 0)

const rootStyle = computed(() => ({
  '--toast-stagger-delay': `${Math.min(props.queueIndex, 12) * 42}ms`,
  /* Le <li> est rendu via Teleport (Reka) : pas d’attribut scoped - forcer le rayon côté racine. */
  borderRadius: '4px',
  overflow: 'hidden',
}))

const activeDurationMs = computed(() => {
  if (!props.item.open) {
    return Number.POSITIVE_INFINITY
  }
  return isQueueHead.value ? props.item.durationMs : Number.POSITIVE_INFINITY
})

function iconForVariant(variant: SourceToastVariant): string {
  switch (variant) {
    case 'success':
      return 'check_circle'
    case 'error':
      return 'report_problem'
    case 'warning':
      return 'warning'
    case 'info':
      return 'info'
    default:
      return 'info'
  }
}

function progressStyle(remaining: number, totalDuration: number): { width: string } {
  if (!isQueueHead.value) {
    return { width: '100%' }
  }
  if (!Number.isFinite(totalDuration) || totalDuration <= 0) {
    return { width: '100%' }
  }
  const ratio = Math.min(1, Math.max(0, remaining / totalDuration))
  return { width: `${ratio * 100}%` }
}

function progressModifierClass(remaining: number, totalDuration: number): string {
  if (!maxDurationVisible(remaining, totalDuration)) {
    return 'source-toast-progress--queued'
  }
  return ''
}

function maxDurationVisible(remaining: number, totalDuration: number): boolean {
  if (!isQueueHead.value) {
    return false
  }
  return Number.isFinite(totalDuration) && totalDuration > 0
}

function onUpdateOpen(open: boolean) {
  onToastOpenChange(props.item, open)
}
</script>

<template>
  <ToastRoot
    :open="item.open"
    class="source-toast-root"
    type="foreground"
    :duration="activeDurationMs"
    :style="rootStyle"
    @update:open="onUpdateOpen"
  >
    <template #default="{ remaining, duration: totalDuration }">
      <div class="source-toast-card" :data-variant="item.variant">
        <div class="source-toast-accent" aria-hidden="true" />
        <div class="source-toast-progress-track" aria-hidden="true">
          <div
            class="source-toast-progress-fill"
            :class="progressModifierClass(remaining, totalDuration)"
            :style="progressStyle(remaining, totalDuration)"
          />
        </div>
        <div class="source-toast-row">
          <span class="material-symbols-rounded source-toast-icon" aria-hidden="true">
            {{ iconForVariant(item.variant) }}
          </span>
          <div class="source-toast-main">
            <ToastTitle class="source-toast-title">
              {{ item.title }}
            </ToastTitle>
            <ToastDescription v-if="item.description" class="source-toast-desc">
              {{ item.description }}
            </ToastDescription>
          </div>
          <ToastClose class="source-toast-close" aria-label="Fermer la notification">
            <span class="material-symbols-rounded">close</span>
          </ToastClose>
        </div>
      </div>
    </template>
  </ToastRoot>
</template>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/colors' as c;

$source-toast-width: 400px;
/* Hauteur minimale (zone texte + barre 3px) ; le contenu peut s’étendre pour afficher tout le texte. */
$source-toast-height: 96px;

/* Styles du <li> (ToastRoot) : voir bloc non-scopé ci-dessous - le Teleport empêche le scoped de s’appliquer. */

.source-toast-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-height: $source-toast-height;
  box-sizing: border-box;
  background: c.$enedis-neutral-20;
  border: 1px solid c.$enedis-neutral-40;
  border-radius: var(--toast-radius);
  box-shadow: var(--toast-shadow);
  position: relative;
  overflow: hidden;
}

.source-toast-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex: 1;
  min-height: 0;
  padding: 16px 24px 16px 16px;
  min-width: 0;
}

.source-toast-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: var(--toast-radius) 0 0 var(--toast-radius);
  pointer-events: none;
}

.source-toast-card[data-variant='success'] .source-toast-accent {
  background: c.$enedis-success-500;
}

.source-toast-card[data-variant='error'] .source-toast-accent {
  background: c.$enedis-error-500;
}

.source-toast-card[data-variant='warning'] .source-toast-accent {
  background: c.$enedis-warning-500;
}

.source-toast-card[data-variant='info'] .source-toast-accent {
  background: #1423DC;
}

.source-toast-icon {
  flex-shrink: 0;
  align-self: flex-start;
  font-size: 20px;
  line-height: 1;
  margin-top: 0;
  font-variation-settings:
    'FILL' 0,
    'wght' 400,
    'GRAD' 0,
    'opsz' 24;
}

.source-toast-card[data-variant='success'] .source-toast-icon {
  color: c.$enedis-success-600;
}

.source-toast-card[data-variant='error'] .source-toast-icon {
  color: c.$enedis-error-500;
}

.source-toast-card[data-variant='warning'] .source-toast-icon {
  color: c.$enedis-warning-600;
}

.source-toast-card[data-variant='info'] .source-toast-icon {
  color: #1423DC;
}

.source-toast-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: flex-start;
  padding-right: 0;
}

.source-toast-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.source-toast-desc {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-secondary);
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.source-toast-progress-track {
  flex-shrink: 0;
  width: 100%;
  height: 3px;
  background: var(--color-border);
  border-radius: var(--toast-radius) var(--toast-radius) 0 0;
  overflow: hidden;
}

.source-toast-progress-fill {
  height: 100%;
  background: var(--color-text-secondary);
  transform-origin: left center;
  transition: width 0.08s linear;
}

.source-toast-card[data-variant='success'] .source-toast-progress-fill:not(.source-toast-progress--queued) {
  background: c.$enedis-success-500;
}

.source-toast-card[data-variant='error'] .source-toast-progress-fill:not(.source-toast-progress--queued) {
  background: c.$enedis-error-500;
}

.source-toast-card[data-variant='warning'] .source-toast-progress-fill:not(.source-toast-progress--queued) {
  background: c.$enedis-warning-500;
}

.source-toast-card[data-variant='info'] .source-toast-progress-fill:not(.source-toast-progress--queued) {
  background: #1423DC;
}

.source-toast-progress--queued {
  opacity: 0.35;
  transition: none;
}

.source-toast-close {
  flex-shrink: 0;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: c.$enedis-neutral-500;
  cursor: pointer;
  border-radius: 4px;
}

.source-toast-close:hover {
  color: c.$enedis-neutral-800;
  background: c.$enedis-neutral-30;
}

.source-toast-close .material-symbols-rounded {
  font-size: 18px;
}
</style>

<!--
  ToastRoot rend un <li> téléporté dans le viewport : les sélecteurs scoped ne matchent pas
  (pas de data-v-* sur ce nœud). Animations + rayon sur le conteneur racine ici.
-->
<style lang="scss">
$source-toast-width: 400px;

.source-toast-root {
  --toast-radius: 4px;
  --toast-shadow: 0 2px 8px 1px rgba(16, 16, 20, 0.16);
  width: 100%;
  max-width: min(#{$source-toast-width}, calc(100vw - 40px));
  box-sizing: border-box;
  border-radius: var(--toast-radius);
  overflow: hidden;
  /* Évite qu’un style inline type transition: 0.15s (Reka / swipe) écrase l’animation d’entrée */
  transition:
    transform 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.28s ease !important;
}

.source-toast-root[data-state='open'] {
  animation: source-toast-enter 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: var(--toast-stagger-delay, 0ms);
}

/* Sortie au-dessus des toasts suivants (surtout le plus ancien en tête de liste). */
.source-toast-root[data-state='closed'] {
  position: relative;
  z-index: 3;
  isolation: isolate;
  animation: source-toast-exit 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes source-toast-enter {
  from {
    opacity: 0;
    transform: translateY(-18px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes source-toast-exit {
  from {
    opacity: 1;
    transform: translateX(0);
  }

  to {
    opacity: 0;
    transform: translateX(calc(100% + 16px));
  }
}
</style>
